// ReportsPage.tsx
import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { apiClient } from '../lib/api'
import {
  ChevronLeft,
  FileText,
  BarChart3,
  Calendar,
  Settings,
  Plus,
  Edit,
  Trash2,
  Search,
  Download,
  RefreshCw,
  Eye,
  Play,
  Copy,
  TrendingUp,
  DollarSign,
  Package,
  Users,
  CreditCard,
  ShoppingCart,
  Maximize,
  Filter,
  Clock,
  CheckCircle,
  AlertCircle,
  PieChart,
  LineChart,
  Activity,
  Layers,
  Grid,
  List,
  Printer,
  Database,
  TrendingDown,
  AlertTriangle,
  Zap,
  FileBarChart,
  X
} from 'lucide-react'
import toast from 'react-hot-toast'

// Custom Components
import { ErrorBoundary } from '../components/ui/ErrorBoundary'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { DataTable, Column } from '../components/ui/DataTable'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { ReportDesigner } from '../components/reports/ReportDesigner'
import ExportDialog from '../components/export/ExportDialog'
import { Button } from '../components/ui/Button'
import { DateRangePicker } from '../components/ui/DateRangePicker'

// Types based on schema
import {
  type User,
  type Product,
  type Category,
  type Order,
  type OrderItem,
  type Payment,
  type PaymentType,
  type BusinessDay,
  type CashRegister
} from '../types'

// Define missing types locally as any for now to resolve compilation errors
type PrintTemplate = any;
type CustomReport = any;
type ZReport = any;
type ShiftReport = any;
type KpiDefinition = any;
type DashboardWidget = any;
type ReportSchedule = any;
type ReportParameter = any;
type ReportTemplate = any;
type ReportElement = any;

// API Response Types
interface ApiResponse<T> {
  data: T[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

interface ReportExecution {
  id: string
  template_id: string
  executed_by?: string
  parameters?: any
  output_format: 'pdf' | 'excel' | 'csv' | 'html'
  status: 'pending' | 'running' | 'completed' | 'failed'
  execution_time?: number
  file_url?: string
  file_size?: number
  error_message?: string
  executed_at: string
  completed_at?: string
  template?: ReportTemplate
}

interface ScheduledReport {
  id: string
  template_id: string
  name: string
  schedule_type: 'daily' | 'weekly' | 'monthly' | 'hourly' | 'custom'
  schedule_config?: any
  parameters?: any
  output_formats?: string[]
  recipients?: string[]
  is_active: boolean
  last_run?: string
  next_run?: string
  created_at: string
  updated_at: string
  template?: ReportTemplate
}

interface FiscalDailyReport {
  id: string
  report_number: number
  z_number?: string
  cash_register_id: string
  business_day_id?: string
  report_date: string
  opening_amount: number
  closing_amount: number
  total_sales: number
  total_tax: number
  total_voided: number
  total_refunds: number
  fiscal_signature?: string
  generated_by?: string
  created_at: string
}

interface CashFlowReport {
  date: string
  cash_in: number
  cash_out: number
  net_cash_flow: number
  opening_balance: number
  closing_balance: number
}

interface SalesReport {
  date: string
  total_sales: number
  total_items: number
  average_ticket: number
  transaction_count: number
  discount_amount: number
  tax_amount: number
  net_sales: number
}

interface InventoryReport {
  product_id: string
  product_name: string
  current_stock: number
  minimum_stock: number
  reorder_level: number
  last_received_date?: string
  last_sold_date?: string
  days_of_stock: number
  status: 'adequate' | 'low' | 'critical' | 'out_of_stock'
}

type TabType =
  | 'templates'
  | 'custom'
  | 'scheduled'
  | 'executions'
  | 'fiscal'
  | 'business'
  | 'sales'
  | 'inventory'
  | 'cashflow'
  | 'kpis'
  | 'dashboards'
  | 'designer'

type TemplateSubTab = 'all' | 'sales' | 'inventory' | 'customer' | 'financial' | 'system'
type FiscalSubTab = 'z-reports' | 'shift-reports' | 'daily-reports' | 'fiscal-documents'
type ExecutionSubTab = 'all' | 'completed' | 'failed' | 'running'
type ReportCategory = 'sales' | 'inventory' | 'financial' | 'customer' | 'operational' | 'compliance'

const ITEMS_PER_PAGE = 50
const DEBOUNCE_DELAY = 500

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('templates')
  const [activeSubTab, setActiveSubTab] = useState<any>('all')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmDialogProps, setConfirmDialogProps] = useState<any>({})
  const [showDesigner, setShowDesigner] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null)
  const [exportFormat, setExportFormat] = useState<'pdf' | 'excel' | 'csv' | 'html'>('pdf')
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
    start: new Date(new Date().setDate(new Date().getDate() - 30)),
    end: new Date()
  })

  // Report Data
  const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>([])
  const [reportElements, setReportElements] = useState<ReportElement[]>([])
  const [customReports, setCustomReports] = useState<CustomReport[]>([])
  const [scheduledReports, setScheduledReports] = useState<ScheduledReport[]>([])
  const [reportExecutions, setReportExecutions] = useState<ReportExecution[]>([])
  const [zReports, setZReports] = useState<ZReport[]>([])
  const [shiftReports, setShiftReports] = useState<ShiftReport[]>([])
  const [fiscalDailyReports, setFiscalDailyReports] = useState<FiscalDailyReport[]>([])
  const [businessDays, setBusinessDays] = useState<BusinessDay[]>([])
  const [kpiDefinitions, setKPIDefinitions] = useState<KpiDefinition[]>([])
  const [dashboardWidgets, setDashboardWidgets] = useState<DashboardWidget[]>([])
  const [cashRegisters, setCashRegisters] = useState<CashRegister[]>([])
  const [salesReportData, setSalesReportData] = useState<SalesReport[]>([])
  const [inventoryReportData, setInventoryReportData] = useState<InventoryReport[]>([])
  const [cashFlowReportData, setCashFlowReportData] = useState<CashFlowReport[]>([])

  // UI State
  const [searchTerm, setSearchTerm] = useState('')
  const [filterOptions, setFilterOptions] = useState<any>({
    category: '',
    is_active: '',
    store_id: '',
    cash_register_id: '',
    status: '',
    date_from: '',
    date_to: ''
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [bulkAction, setBulkAction] = useState<string>('')
  const [refreshKey, setRefreshKey] = useState(0)

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm || dateRange.start || dateRange.end) {
        setCurrentPage(1)
        fetchData()
      }
    }, DEBOUNCE_DELAY)
    return () => clearTimeout(timer)
  }, [searchTerm, dateRange, refreshKey])

  // Fetch data on tab/subtab change
  useEffect(() => {
    fetchData()
  }, [activeTab, activeSubTab, currentPage, filterOptions])

  // Fetch cash registers for filters
  useEffect(() => {
    fetchCashRegisters()
  }, [])

  const fetchCashRegisters = async () => {
    try {
      const response = await apiClient.get('/api/cash-registers?is_active=true')
      setCashRegisters(response.data?.data || [])
    } catch (error) {
      console.error('Error fetching cash registers:', error)
    }
  }

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    setSelectedItems([])

    try {
      switch (activeTab) {
        case 'templates':
          await fetchTemplatesData()
          break
        case 'custom':
          await fetchCustomReportsData()
          break
        case 'scheduled':
          await fetchScheduledReportsData()
          break
        case 'executions':
          await fetchExecutionsData()
          break
        case 'fiscal':
          await fetchFiscalReportsData()
          break
        case 'business':
          await fetchBusinessDaysData()
          break
        case 'sales':
          await fetchSalesReportData()
          break
        case 'inventory':
          await fetchInventoryReportData()
          break
        case 'cashflow':
          await fetchCashFlowReportData()
          break
        case 'kpis':
          await fetchKPIsData()
          break
        case 'dashboards':
          await fetchDashboardsData()
          break
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load data'
      setError(errorMessage)
      toast.error(errorMessage)
      console.error('Error fetching data:', err)
    } finally {
      setLoading(false)
    }
  }, [activeTab, activeSubTab, currentPage, searchTerm, filterOptions, dateRange])

  const fetchTemplatesData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm,
      ...filterOptions
    })

    if (activeSubTab !== 'all') {
      params.append('category', activeSubTab)
    }

    const response = await apiClient.get<ApiResponse<ReportTemplate>>(`/api/reports/templates?${params}`)
    setReportTemplates(response.data.data)
    setTotalCount(response.data.total)
  }

  const fetchCustomReportsData = async () => {
    const response = await apiClient.get<ApiResponse<CustomReport>>('/api/reports/custom')
    setCustomReports(response?.data?.data || [])
  }

  const fetchScheduledReportsData = async () => {
    const response = await apiClient.get<ApiResponse<ScheduledReport>>('/api/reports/schedules')
    setScheduledReports(response?.data?.data || [])
  }

  const fetchExecutionsData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm
    })

    if (activeSubTab !== 'all') {
      params.append('status', activeSubTab)
    }

    if (dateRange.start) {
      params.append('date_from', dateRange.start.toISOString().split('T')[0])
    }
    if (dateRange.end) {
      params.append('date_to', dateRange.end.toISOString().split('T')[0])
    }

    const response = await apiClient.get<ApiResponse<ReportExecution>>(`/api/reports/executions?${params}`)
    setReportExecutions(response.data.data)
    setTotalCount(response.data.total)
  }

  const fetchFiscalReportsData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString()
    })

    if (filterOptions.cash_register_id) {
      params.append('cash_register_id', filterOptions.cash_register_id)
    }

    if (dateRange.start) {
      params.append('date_from', dateRange.start.toISOString().split('T')[0])
    }
    if (dateRange.end) {
      params.append('date_to', dateRange.end.toISOString().split('T')[0])
    }

    if (activeSubTab === 'z-reports') {
      const response = await apiClient.get<ApiResponse<ZReport>>(`/api/reports/fiscal/z-reports?${params}`)
      setZReports(response.data.data)
    } else if (activeSubTab === 'shift-reports') {
      const response = await apiClient.get<ApiResponse<ShiftReport>>(`/api/reports/fiscal/shift-reports?${params}`)
      setShiftReports(response.data.data)
    } else if (activeSubTab === 'daily-reports') {
      const response = await apiClient.get<ApiResponse<FiscalDailyReport>>(`/api/reports/fiscal/daily?${params}`)
      setFiscalDailyReports(response.data.data)
    }
  }

  const fetchBusinessDaysData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString()
    })

    if (filterOptions.status) {
      params.append('status', filterOptions.status)
    }
    if (filterOptions.cash_register_id) {
      params.append('cash_register_id', filterOptions.cash_register_id)
    }

    if (dateRange.start) {
      params.append('date_from', dateRange.start.toISOString().split('T')[0])
    }
    if (dateRange.end) {
      params.append('date_to', dateRange.end.toISOString().split('T')[0])
    }

    const response = await apiClient.get<ApiResponse<BusinessDay>>(`/api/business-days?${params}`)
    setBusinessDays(response.data.data)
    setTotalCount(response.data.total)
  }

  const fetchSalesReportData = async () => {
    const params = new URLSearchParams({
      group_by: 'day'
    })

    if (filterOptions.cash_register_id) {
      params.append('cash_register_id', filterOptions.cash_register_id)
    }
    if (dateRange.start) {
      params.append('start_date', dateRange.start.toISOString().split('T')[0])
    }
    if (dateRange.end) {
      params.append('end_date', dateRange.end.toISOString().split('T')[0])
    }

    const response = await apiClient.get<ApiResponse<SalesReport>>(`/api/reports/sales?${params}`)
    setSalesReportData(response.data.data)
  }

  const fetchInventoryReportData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm
    })

    if (filterOptions.status) {
      params.append('stock_status', filterOptions.status)
    }

    const response = await apiClient.get<ApiResponse<InventoryReport>>(`/api/reports/inventory?${params}`)
    setInventoryReportData(response.data.data)
    setTotalCount(response.data.total)
  }

  const fetchCashFlowReportData = async () => {
    const params = new URLSearchParams({
      group_by: 'day'
    })

    if (dateRange.start) {
      params.append('start_date', dateRange.start.toISOString().split('T')[0])
    }
    if (dateRange.end) {
      params.append('end_date', dateRange.end.toISOString().split('T')[0])
    }

    const response = await apiClient.get<ApiResponse<CashFlowReport>>(`/api/reports/cashflow?${params}`)
    setCashFlowReportData(response.data.data)
  }

  const fetchKPIsData = async () => {
    const response = await apiClient.get<ApiResponse<KpiDefinition>>('/api/reports/kpis/definitions')
    setKPIDefinitions(response.data.data)
  }

  const fetchDashboardsData = async () => {
    const response = await apiClient.get<ApiResponse<DashboardWidget>>('/api/reports/dashboard-widgets')
    setDashboardWidgets(response.data.data)
  }

  const handleExecuteReport = async (templateId: string, format?: string) => {
    try {
      const response = await apiClient.post(`/api/reports/execute/${templateId}`, {
        output_format: format || exportFormat,
        parameters: {},
        include_data: true
      })

      if (response.data.file_url) {
        window.open(response.data.file_url, '_blank')
        toast.success('Report generated and downloaded successfully')
      } else {
        toast.success('Report execution started')
      }

      // Refresh executions list
      setTimeout(() => fetchExecutionsData(), 1000)
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to execute report'
      toast.error(errorMsg)
      console.error('Error executing report:', err)
    }
  }

  const handleExportData = async (format: 'pdf' | 'excel' | 'csv' | 'html') => {
    try {
      let endpoint = ''
      let params = new URLSearchParams()

      switch (activeTab) {
        case 'sales':
          endpoint = '/api/reports/sales/export'
          break
        case 'inventory':
          endpoint = '/api/reports/inventory/export'
          break
        case 'cashflow':
          endpoint = '/api/reports/cashflow/export'
          break
        case 'fiscal':
          if (activeSubTab === 'z-reports') {
            endpoint = '/api/reports/fiscal/z-reports/export'
          }
          break
        default:
          toast.error('Export not available for this report type')
          return
      }

      params.append('format', format)

      if (dateRange.start) {
        params.append('start_date', dateRange.start.toISOString().split('T')[0])
      }
      if (dateRange.end) {
        params.append('end_date', dateRange.end.toISOString().split('T')[0])
      }

      const response = await apiClient.get(`${endpoint}?${params}`, {
        responseType: 'blob'
      })

      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${activeTab}-report-${new Date().toISOString().split('T')[0]}.${format}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      toast.success(`Report exported as ${format.toUpperCase()}`)
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to export report')
    }
  }

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      await apiClient.delete(`/api/reports/templates/${templateId}`)
      toast.success('Template deleted successfully')
      fetchTemplatesData()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to delete template')
    }
  }

  const handleBulkAction = async () => {
    if (!selectedItems.length) {
      toast.error('No items selected')
      return
    }

    switch (bulkAction) {
      case 'delete':
        openConfirmDialog({
          title: 'Delete Selected Items',
          message: `Are you sure you want to delete ${selectedItems.length} item(s)?`,
          onConfirm: async () => {
            try {
              await apiClient.post('/api/reports/bulk-delete', { ids: selectedItems })
              toast.success(`${selectedItems.length} item(s) deleted`)
              setSelectedItems([])
              setBulkAction('')
              fetchData()
            } catch (err: any) {
              toast.error(err.response?.data?.error || 'Failed to delete items')
            }
          }
        })
        break
      case 'export':
        // Handle bulk export
        break
    }
  }

  const handleMaximize = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  const openConfirmDialog = (props: any) => {
    setConfirmDialogProps(props)
    setShowConfirmDialog(true)
  }

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false)
    setConfirmDialogProps({})
  }

  const confirmAction = () => {
    if (confirmDialogProps.onConfirm) {
      confirmDialogProps.onConfirm()
    }
    closeConfirmDialog()
  }

  const templateColumns: Column<ReportTemplate>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Report Name',
      accessor: (row) => row.name,
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div className="min-w-[200px]">
          <div className="text-white font-medium truncate">{value}</div>
          {row.description && <div className="text-xs text-gray-500 truncate">{row.description}</div>}
        </div>
      )
    },
    {
      key: 'template_type',
      header: 'Type',
      accessor: (row) => row.template_type,
      sortable: true,
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full capitalize ${
          value === 'receipt' ? 'bg-blue-900 text-blue-200' :
          value === 'invoice' ? 'bg-purple-900 text-purple-200' :
          value === 'report' ? 'bg-green-900 text-green-200' :
          'bg-gray-700 text-gray-300'
        }`}>
          {value}
        </span>
      )
    },
    {
      key: 'is_default',
      header: 'Default',
      accessor: (row) => row.is_default,
      align: 'center',
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value ? 'bg-yellow-900 text-yellow-200' : 'bg-gray-700 text-gray-400'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      key: 'is_active',
      header: 'Status',
      accessor: (row) => row.is_active,
      align: 'center',
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
      sortable: true
    },
    {
      key: 'created_at',
      header: 'Created',
      accessor: (row) => row.created_at,
      render: (value) => new Date(value).toLocaleDateString(),
      sortable: true
    }
  ], [])

  const executionColumns: Column<ReportExecution>[] = useMemo(() => [
    {
      key: 'executed_at',
      header: 'Date/Time',
      accessor: (row) => row.executed_at,
      sortable: true,
      render: (value) => (
        <div className="min-w-[120px]">
          <div className="text-white">{new Date(value).toLocaleDateString()}</div>
          <div className="text-xs text-gray-500">{new Date(value).toLocaleTimeString()}</div>
        </div>
      )
    },
    {
      key: 'template',
      header: 'Report',
      accessor: (row) => row.template?.name || '-',
      sortable: true,
      filterable: true
    },
    {
      key: 'output_format',
      header: 'Format',
      accessor: (row) => row.output_format,
      render: (value) => (
        <span className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded uppercase">
          {value}
        </span>
      )
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => row.status,
      align: 'center',
      render: (value) => {
        const statusConfig = {
          completed: { bg: 'bg-green-900', text: 'text-green-200', label: 'Completed' },
          failed: { bg: 'bg-red-900', text: 'text-red-200', label: 'Failed' },
          running: { bg: 'bg-yellow-900', text: 'text-yellow-200', label: 'Running' },
          pending: { bg: 'bg-gray-700', text: 'text-gray-400', label: 'Pending' }
        }
        const config = statusConfig[value as keyof typeof statusConfig] || statusConfig.pending
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${config.bg} ${config.text}`}>
            {config.label}
          </span>
        )
      },
      sortable: true
    },
    {
      key: 'execution_time',
      header: 'Duration',
      accessor: (row) => row.execution_time,
      align: 'right',
      render: (value) => value ? `${value}ms` : '-'
    },
    {
      key: 'file_size',
      header: 'Size',
      accessor: (row) => row.file_size,
      align: 'right',
      render: (value) => value ? `${(value / 1024).toFixed(2)} KB` : '-'
    }
  ], [])

  const salesColumns: Column<SalesReport>[] = useMemo(() => [
    {
      key: 'date',
      header: 'Date',
      accessor: (row) => row.date,
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      key: 'total_sales',
      header: 'Total Sales',
      accessor: (row) => row.total_sales,
      align: 'right',
      render: (value) => (
        <div className="font-medium text-green-400">
          ${parseFloat(value.toString()).toFixed(2)}
        </div>
      )
    },
    {
      key: 'net_sales',
      header: 'Net Sales',
      accessor: (row) => row.net_sales,
      align: 'right',
      render: (value) => `$${parseFloat(value.toString()).toFixed(2)}`
    },
    {
      key: 'transaction_count',
      header: 'Transactions',
      accessor: (row) => row.transaction_count,
      align: 'right',
      render: (value) => value.toLocaleString()
    },
    {
      key: 'average_ticket',
      header: 'Avg Ticket',
      accessor: (row) => row.average_ticket,
      align: 'right',
      render: (value) => `$${parseFloat(value.toString()).toFixed(2)}`
    },
    {
      key: 'total_items',
      header: 'Items Sold',
      accessor: (row) => row.total_items,
      align: 'right',
      render: (value) => value.toLocaleString()
    }
  ], [])

  const inventoryColumns: Column<InventoryReport>[] = useMemo(() => [
    {
      key: 'product_name',
      header: 'Product',
      accessor: (row) => row.product_name,
      sortable: true,
      filterable: true
    },
    {
      key: 'current_stock',
      header: 'Current Stock',
      accessor: (row) => row.current_stock,
      align: 'right',
      render: (value) => parseFloat(value.toString()).toFixed(2)
    },
    {
      key: 'minimum_stock',
      header: 'Min Stock',
      accessor: (row) => row.minimum_stock,
      align: 'right',
      render: (value) => parseFloat(value.toString()).toFixed(2)
    },
    {
      key: 'days_of_stock',
      header: 'Days of Stock',
      accessor: (row) => row.days_of_stock,
      align: 'right',
      render: (value) => (
        <span className={`font-medium ${
          parseFloat(value.toString()) > 30 ? 'text-green-400' :
          parseFloat(value.toString()) > 7 ? 'text-yellow-400' :
          'text-red-400'
        }`}>
          {parseFloat(value.toString()).toFixed(1)}
        </span>
      )
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => row.status,
      align: 'center',
      render: (value) => {
        const statusConfig = {
          adequate: { bg: 'bg-green-900', text: 'text-green-200', label: 'Adequate' },
          low: { bg: 'bg-yellow-900', text: 'text-yellow-200', label: 'Low' },
          critical: { bg: 'bg-red-900', text: 'text-red-200', label: 'Critical' },
          out_of_stock: { bg: 'bg-gray-700', text: 'text-gray-400', label: 'Out of Stock' }
        }
        const config = statusConfig[value as keyof typeof statusConfig] || statusConfig.adequate
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${config.bg} ${config.text}`}>
            {config.label}
          </span>
        )
      }
    },
    {
      key: 'last_sold_date',
      header: 'Last Sold',
      accessor: (row) => row.last_sold_date,
      render: (value) => value ? new Date(value).toLocaleDateString() : 'Never'
    }
  ], [])

  const zReportColumns: Column<ZReport>[] = useMemo(() => [
    {
      key: 'report_number',
      header: 'Report #',
      accessor: (row) => row.report_number,
      sortable: true,
      render: (value) => <span className="font-mono">Z-{value}</span>
    },
    {
      key: 'opened_date',
      header: 'Date',
      accessor: (row) => row.opened_date,
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      key: 'total_sales',
      header: 'Total Sales',
      accessor: (row) => row.total_sales,
      align: 'right',
      render: (value) => (
        <div className="font-medium text-white">
          ${parseFloat(value.toString()).toFixed(2)}
        </div>
      )
    },
    {
      key: 'total_cash',
      header: 'Cash',
      accessor: (row) => row.total_cash,
      align: 'right',
      render: (value) => `$${parseFloat(value.toString()).toFixed(2)}`
    },
    {
      key: 'total_card',
      header: 'Card',
      accessor: (row) => row.total_card,
      align: 'right',
      render: (value) => `$${parseFloat(value.toString()).toFixed(2)}`
    },
    {
      key: 'total_transactions',
      header: 'Transactions',
      accessor: (row) => row.total_transactions,
      align: 'right',
      render: (value) => value.toLocaleString()
    },
    {
      key: 'cash_difference',
      header: 'Cash Diff',
      accessor: (row) => row.cash_difference,
      align: 'right',
      render: (value) => {
        const amount = parseFloat(value.toString())
        return (
          <span className={`font-medium ${amount === 0 ? 'text-green-400' : 'text-red-400'}`}>
            ${amount.toFixed(2)}
          </span>
        )
      }
    }
  ], [])

  const renderContent = () => {
    if (showDesigner) {
      return (
        <div className="h-full">
          <div className="mb-4 flex items-center justify-between">
            <button
              onClick={() => {
                setShowDesigner(false)
                setSelectedTemplate(null)
              }}
              className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 hover:text-white transition-colors"
            >
              <ChevronLeft size={20} />
              Back to Reports
            </button>
          </div>
          <ReportDesigner
            template={selectedTemplate || undefined}
            onSave={async (template, elements) => {
              try {
                if (selectedTemplate) {
                  await apiClient.put(`/api/reports/templates/${selectedTemplate.id}`, {
                    ...template,
                    elements
                  })
                  toast.success('Report template updated')
                } else {
                  await apiClient.post('/api/reports/templates', {
                    ...template,
                    elements
                  })
                  toast.success('Report template created')
                }
                setShowDesigner(false)
                setSelectedTemplate(null)
                fetchTemplatesData()
              } catch (err: any) {
                toast.error(err.response?.data?.error || 'Failed to save template')
              }
            }}
          />
        </div>
      )
    }

    switch (activeTab) {
      case 'templates':
        return renderTemplatesContent()
      case 'custom':
        return renderCustomReportsContent()
      case 'scheduled':
        return renderScheduledReportsContent()
      case 'executions':
        return renderExecutionsContent()
      case 'fiscal':
        return renderFiscalReportsContent()
      case 'business':
        return renderBusinessDaysContent()
      case 'sales':
        return renderSalesReportContent()
      case 'inventory':
        return renderInventoryReportContent()
      case 'cashflow':
        return renderCashFlowReportContent()
      case 'kpis':
        return renderKPIsContent()
      case 'dashboards':
        return renderDashboardsContent()
      default:
        return (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <FileBarChart size={64} className="mx-auto text-gray-600 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Select a Report Type</h3>
              <p className="text-gray-400">Choose a report category from the tabs above</p>
            </div>
          </div>
        )
    }
  }

  const renderTemplatesContent = () => {
    const subTabs = [
      { id: 'all', label: 'All Templates', icon: FileText },
      { id: 'sales', label: 'Sales', icon: DollarSign },
      { id: 'inventory', label: 'Inventory', icon: Package },
      { id: 'customer', label: 'Customer', icon: Users },
      { id: 'financial', label: 'Financial', icon: CreditCard },
      { id: 'system', label: 'System', icon: Settings }
    ]

    return (
      <div className="space-y-6">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id)
                  setCurrentPage(1)
                }}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400 bg-gray-750'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-750'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>

        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Filter size={18} />
              Filters
            </h3>
            <button
              onClick={() => {
                setFilterOptions({
                  category: '',
                  is_active: '',
                  store_id: '',
                  cash_register_id: ''
                })
                setSearchTerm('')
                setCurrentPage(1)
              }}
              className="text-sm text-blue-400 hover:text-blue-300 px-3 py-1 hover:bg-blue-900/20 rounded"
            >
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Status</label>
              <select
                value={filterOptions.is_active}
                onChange={(e) => {
                  setFilterOptions({...filterOptions, is_active: e.target.value})
                  setCurrentPage(1)
                }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Category</label>
              <select
                value={filterOptions.category}
                onChange={(e) => {
                  setFilterOptions({...filterOptions, category: e.target.value})
                  setCurrentPage(1)
                }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                <option value="">All Categories</option>
                <option value="sales">Sales</option>
                <option value="inventory">Inventory</option>
                <option value="financial">Financial</option>
                <option value="customer">Customer</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Cash Register</label>
              <select
                value={filterOptions.cash_register_id}
                onChange={(e) => {
                  setFilterOptions({...filterOptions, cash_register_id: e.target.value})
                  setCurrentPage(1)
                }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                <option value="">All Registers</option>
                {cashRegisters.map(register => (
                  <option key={register.id} value={register.id}>
                    {register.name} ({register.register_number})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search templates by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            />
          </div>
          <div className="flex gap-2">
            {selectedItems.length > 0 && (
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Bulk Actions</option>
                <option value="export">Export Selected</option>
                <option value="delete">Delete Selected</option>
              </select>
            )}
            {bulkAction && selectedItems.length > 0 && (
              <button
                onClick={handleBulkAction}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
              >
                Apply
              </button>
            )}
            <button
              onClick={() => {
                setSelectedTemplate(null)
                setShowDesigner(true)
              }}
              className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors font-medium"
            >
              <Plus size={18} />
              New Template
            </button>
          </div>
        </div>

        {loading && !reportTemplates.length ? (
          <div className="flex justify-center items-center min-h-[300px]">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="bg-red-900/20 border border-red-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <AlertCircle className="text-red-400" size={20} />
              <h3 className="text-white font-medium">Error Loading Templates</h3>
            </div>
            <p className="text-gray-400">{error}</p>
            <button
              onClick={fetchData}
              className="mt-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Retry
            </button>
          </div>
        ) : (
          <DataTable<ReportTemplate>
            data={reportTemplates}
            columns={templateColumns}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            selectable={true}
            onSelectionChange={setSelectedItems}
            actions={(row) => (
              <div className="flex items-center gap-1">
                <button
                  className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group"
                  title="Execute Report"
                  onClick={() => handleExecuteReport(row.id)}
                >
                  <Play size={16} className="text-green-400 group-hover:text-green-300" />
                </button>
                <button
                  className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group"
                  title="View/Edit"
                  onClick={() => {
                    setSelectedTemplate(row)
                    setShowDesigner(true)
                  }}
                >
                  <Eye size={16} className="text-blue-400 group-hover:text-blue-300" />
                </button>
                <button
                  className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group"
                  title="Duplicate"
                  onClick={() => {
                    // Handle duplicate
                  }}
                >
                  <Copy size={16} className="text-yellow-400 group-hover:text-yellow-300" />
                </button>
                {!row.is_default && (
                  <button
                    className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group"
                    title="Delete"
                    onClick={() => openConfirmDialog({
                      title: 'Delete Template',
                      message: `Are you sure you want to delete "${row.name}"? This action cannot be undone.`,
                      confirmText: 'Delete',
                      cancelText: 'Cancel',
                      type: 'danger',
                      onConfirm: () => handleDeleteTemplate(row.id)
                    })}
                  >
                    <Trash2 size={16} className="text-red-400 group-hover:text-red-300" />
                  </button>
                )}
              </div>
            )}
            emptyState={
              <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
                <FileText size={64} className="mx-auto text-gray-600 mb-6" />
                <div className="text-gray-300 text-xl font-medium mb-3">No report templates found</div>
                <div className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                  {searchTerm || Object.values(filterOptions).some(v => v)
                    ? 'Try adjusting your search or filters'
                    : 'Create your first custom report template to get started'
                  }
                </div>
                {!searchTerm && !Object.values(filterOptions).some(v => v) && (
                  <button
                    onClick={() => setShowDesigner(true)}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                  >
                    <Plus size={18} className="inline mr-2" />
                    Create Template
                  </button>
                )}
              </div>
            }
          />
        )}
        <ExportDialog
          isOpen={isExportDialogOpen}
          onClose={() => setIsExportDialogOpen(false)}
          entityType="products"
          data={reportTemplates}
          availableFields={['id', 'name', 'category', 'description', 'report_type', 'is_active']}
        />
      </div>
    )
  }

  const renderCustomReportsContent = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-semibold text-white mb-1">Custom SQL Reports</h3>
          <p className="text-gray-400 text-sm">Create and manage custom SQL-based reports</p>
        </div>
        <button className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors font-medium">
          <Plus size={18} />
          New SQL Report
        </button>
      </div>

      <div className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-750">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {customReports.map(report => (
                <tr key={report.id} className="hover:bg-gray-750 transition-colors">
                  <td className="px-6 py-4">
                    <code className="text-sm text-gray-300 font-mono bg-gray-900 px-2 py-1 rounded">
                      {report.report_code}
                    </code>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-white font-medium">{report.report_name}</div>
                      {report.description && (
                        <div className="text-sm text-gray-500 mt-1">{report.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-300 capitalize">{report.report_type}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-3 py-1.5 text-xs rounded-full font-medium ${
                      report.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
                    }`}>
                      {report.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group" title="Execute">
                        <Play size={16} className="text-green-400 group-hover:text-green-300" />
                      </button>
                      <button className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group" title="Edit">
                        <Edit size={16} className="text-yellow-400 group-hover:text-yellow-300" />
                      </button>
                      <button className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group" title="Delete">
                        <Trash2 size={16} className="text-red-400 group-hover:text-red-300" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {customReports.length === 0 && (
          <div className="p-12 text-center">
            <Database size={64} className="mx-auto text-gray-600 mb-4" />
            <div className="text-gray-400 text-lg mb-2">No custom reports found</div>
            <div className="text-gray-500 text-sm">Create custom SQL reports for advanced analytics</div>
          </div>
        )}
      </div>
    </div>
  )

  const renderScheduledReportsContent = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-semibold text-white mb-1">Scheduled Reports</h3>
          <p className="text-gray-400 text-sm">Automated report generation and distribution</p>
        </div>
        <button className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors font-medium">
          <Plus size={18} />
          New Schedule
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {scheduledReports.map(schedule => (
          <div key={schedule.id} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-colors">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-white font-medium mb-1">{schedule.name}</h4>
                <p className="text-sm text-gray-400">{schedule.template?.name}</p>
              </div>
              <span className={`px-3 py-1.5 text-xs rounded-full font-medium ${
                schedule.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
              }`}>
                {schedule.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <span className="text-gray-500 block mb-1">Schedule:</span>
                <span className="text-white capitalize">{schedule.schedule_type}</span>
              </div>
              <div>
                <span className="text-gray-500 block mb-1">Last Run:</span>
                <span className="text-white">
                  {schedule.last_run ? new Date(schedule.last_run).toLocaleString() : 'Never'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block mb-1">Next Run:</span>
                <span className="text-white">
                  {schedule.next_run ? new Date(schedule.next_run).toLocaleString() : '-'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block mb-1">Recipients:</span>
                <span className="text-white">
                  {schedule.recipients?.length || 0}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-4 border-t border-gray-700">
              <button className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg font-medium transition-colors">
                Run Now
              </button>
              <button className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg font-medium transition-colors">
                Edit
              </button>
              <button className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg font-medium transition-colors ml-auto">
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderExecutionsContent = () => {
    const subTabs = [
      { id: 'all', label: 'All Executions', icon: Activity },
      { id: 'completed', label: 'Completed', icon: CheckCircle },
      { id: 'failed', label: 'Failed', icon: AlertCircle },
      { id: 'running', label: 'Running', icon: Clock }
    ]

    return (
      <div className="space-y-6">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id)
                  setCurrentPage(1)
                }}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400 bg-gray-750'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-750'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <DateRangePicker
            value={[dateRange.start, dateRange.end]}
            onChange={(range) => {
              if (range) {
                setDateRange({ start: range[0], end: range[1] })
                setCurrentPage(1)
              }
            }}
            className="w-full sm:w-auto"
          />
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExportDialogOpen(true)}
              disabled={!reportExecutions.length}
              className="flex items-center gap-2"
            >
              <Download size={16} />
              Export
            </Button>
            <button
              onClick={fetchData}
              className="p-2.5 hover:bg-gray-700 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw size={18} className="text-gray-400" />
            </button>
          </div>
        </div>

        <DataTable
          data={reportExecutions}
          columns={executionColumns}
          loading={loading}
          error={error}
          pageSize={ITEMS_PER_PAGE}
          totalCount={totalCount}
          onPageChange={setCurrentPage}
          rowKey={(row) => row.id}
          actions={(row) => (
            <div className="flex items-center gap-1">
              {row.status === 'completed' && row.file_url && (
                <button
                  className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group"
                  title="Download"
                  onClick={() => window.open(row.file_url, '_blank')}
                >
                  <Download size={16} className="text-green-400 group-hover:text-green-300" />
                </button>
              )}
              {row.status === 'failed' && row.error_message && (
                <button
                  className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group"
                  title="View Error"
                  onClick={() => {
                    openConfirmDialog({
                      title: 'Execution Error',
                      message: row.error_message,
                      confirmText: 'Close',
                      cancelText: null,
                      variant: 'info'
                    })
                  }}
                >
                  <AlertCircle size={16} className="text-red-400 group-hover:text-red-300" />
                </button>
              )}
            </div>
          )}
        />
      </div>
    )
  }

  const renderFiscalReportsContent = () => {
    const subTabs = [
      { id: 'z-reports', label: 'Z-Reports', icon: FileBarChart },
      { id: 'shift-reports', label: 'Shift Reports', icon: Users },
      { id: 'daily-reports', label: 'Daily Reports', icon: Calendar },
      { id: 'fiscal-documents', label: 'Fiscal Documents', icon: FileText }
    ]

    return (
      <div className="space-y-6">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id)
                  setCurrentPage(1)
                }}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400 bg-gray-750'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-750'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>

        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h3 className="text-white font-semibold mb-1">Filters</h3>
              <p className="text-gray-400 text-sm">Filter fiscal reports by date and register</p>
            </div>
            <div className="flex items-center gap-2">
              <DateRangePicker
                value={[dateRange.start, dateRange.end]}
                onChange={(range) => {
                  setDateRange({ start: range[0], end: range[1] })
                  setCurrentPage(1)
                }}
              />
              <Button
                onClick={() => setIsExportDialogOpen(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Download size={18} />
                Export
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Cash Register</label>
              <select
                value={filterOptions.cash_register_id}
                onChange={(e) => {
                  setFilterOptions({...filterOptions, cash_register_id: e.target.value})
                  setCurrentPage(1)
                }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Registers</option>
                {cashRegisters.map(register => (
                  <option key={register.id} value={register.id}>
                    {register.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {activeSubTab === 'z-reports' && (
          <DataTable
            data={zReports}
            columns={zReportColumns}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            actions={(row) => (
              <div className="flex items-center gap-1">
                <button
                  className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group"
                  title="View Details"
                  onClick={() => {
                    // Handle view details
                  }}
                >
                  <Eye size={16} className="text-blue-400 group-hover:text-blue-300" />
                </button>
                <button
                  className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group"
                  title="Print"
                  onClick={() => window.print()}
                >
                  <Printer size={16} className="text-gray-400 group-hover:text-gray-300" />
                </button>
              </div>
            )}
          />
        )}
      </div>
    )
  }

  const renderBusinessDaysContent = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-semibold text-white mb-1">Business Days</h3>
          <p className="text-gray-400 text-sm">Daily business operations and summaries</p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker
            value={[dateRange.start, dateRange.end]}
            onChange={(range) => {
              if (range) {
                setDateRange({ start: range[0], end: range[1] })
                setCurrentPage(1)
              }
            }}
          />
              <Button
                onClick={() => setIsExportDialogOpen(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Download size={18} />
                Export
              </Button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-750">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Total Sales</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Transactions</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Cash</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {businessDays.map(day => (
                <tr key={day.id} className="hover:bg-gray-750 transition-colors">
                  <td className="px-6 py-4">
                    <div className="text-white">{new Date((day as any).opened_at || '').toLocaleDateString()}</div>
                    <div className="text-xs text-gray-500">
                      {(day as any).shift_number ? `Shift ${(day as any).shift_number}` : 'Full Day'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1.5 text-xs rounded-full font-medium ${
                      day.status === 'closed' ? 'bg-green-900 text-green-200' :
                      day.status === 'open' ? 'bg-yellow-900 text-yellow-200' :
                      'bg-gray-700 text-gray-400'
                    }`}>
                      {day.status?.toUpperCase() || 'UNKNOWN'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-white font-medium">
                      ${parseFloat((day as any).total_sales?.toString() || '0').toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-white">{(day as any).transaction_count || 0}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-white">
                      ${parseFloat((day as any).actual_cash?.toString() || '0').toFixed(2)}
                    </div>
                    {(day as any).difference && Math.abs(parseFloat((day as any).difference.toString())) > 0 && (
                      <div className={`text-xs ${parseFloat((day as any).difference.toString()) > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        {parseFloat((day as any).difference.toString()) > 0 ? '+' : ''}
                        ${Math.abs(parseFloat((day as any).difference.toString())).toFixed(2)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group" title="View">
                        <Eye size={16} className="text-blue-400 group-hover:text-blue-300" />
                      </button>
                      <button className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group" title="Print Z-Report">
                        <Printer size={16} className="text-gray-400 group-hover:text-gray-300" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderSalesReportContent = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-semibold text-white mb-1">Sales Analytics</h3>
          <p className="text-gray-400 text-sm">Detailed sales performance and trends</p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker
            value={[dateRange.start, dateRange.end]}
            onChange={(range) => {
              if (range) {
                setDateRange({ start: range[0], end: range[1] })
                setCurrentPage(1)
              }
            }}
          />
              <Button
                onClick={() => setIsExportDialogOpen(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Download size={18} />
                Export
              </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm">Total Sales</div>
            <DollarSign size={20} className="text-green-400" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">
            ${salesReportData.reduce((sum, day) => sum + parseFloat(day.total_sales.toString()), 0).toFixed(2)}
          </div>
          <div className="text-sm text-gray-400">Period total</div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm">Avg Daily Sales</div>
            <TrendingUp size={20} className="text-blue-400" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">
            ${salesReportData.length > 0 
              ? (salesReportData.reduce((sum, day) => sum + parseFloat(day.total_sales.toString()), 0) / salesReportData.length).toFixed(2)
              : '0.00'
            }
          </div>
          <div className="text-sm text-gray-400">Per day average</div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm">Transactions</div>
            <CreditCard size={20} className="text-purple-400" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">
            {salesReportData.reduce((sum, day) => sum + day.transaction_count, 0).toLocaleString()}
          </div>
          <div className="text-sm text-gray-400">Total transactions</div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm">Avg Ticket</div>
            <ShoppingCart size={20} className="text-yellow-400" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">
            ${salesReportData.length > 0
              ? (salesReportData.reduce((sum, day) => sum + parseFloat(day.average_ticket.toString()), 0) / salesReportData.length).toFixed(2)
              : '0.00'
            }
          </div>
          <div className="text-sm text-gray-400">Average per transaction</div>
        </div>
      </div>

      <DataTable
        data={salesReportData}
        columns={salesColumns}
        loading={loading}
        error={error}
        rowKey={(row) => row.date}
        actions={(row) => (
          <button
            className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group"
            title="View Details"
            onClick={() => {
              // Handle view daily details
            }}
          >
            <Eye size={16} className="text-blue-400 group-hover:text-blue-300" />
          </button>
        )}
      />
    </div>
  )

  const renderInventoryReportContent = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-semibold text-white mb-1">Inventory Status</h3>
          <p className="text-gray-400 text-sm">Stock levels and replenishment needs</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
            />
          </div>
              <Button
                onClick={() => setIsExportDialogOpen(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Download size={18} />
                Export
              </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm">Total Products</div>
            <Package size={20} className="text-blue-400" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">
            {inventoryReportData.length.toLocaleString()}
          </div>
          <div className="text-sm text-gray-400">Tracked items</div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm">Low Stock</div>
            <AlertTriangle size={20} className="text-yellow-400" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">
            {inventoryReportData.filter(p => p.status === 'low').length.toLocaleString()}
          </div>
          <div className="text-sm text-gray-400">Need reorder</div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm">Critical</div>
            <AlertTriangle size={20} className="text-red-400" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">
            {inventoryReportData.filter(p => p.status === 'critical').length.toLocaleString()}
          </div>
          <div className="text-sm text-gray-400">Urgent attention</div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm">Out of Stock</div>
            <X size={20} className="text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">
            {inventoryReportData.filter(p => p.status === 'out_of_stock').length.toLocaleString()}
          </div>
          <div className="text-sm text-gray-400">No stock available</div>
        </div>
      </div>

      <DataTable
        data={inventoryReportData}
        columns={inventoryColumns}
        loading={loading}
        error={error}
        pageSize={ITEMS_PER_PAGE}
        totalCount={totalCount}
        onPageChange={setCurrentPage}
        rowKey={(row) => row.product_id}
        selectable={true}
        onSelectionChange={(selected) => setSelectedItems(selected.map(item => item.product_id))}
        actions={(row) => (
          <button
            className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors group"
            title="View Product"
            onClick={() => {
              // Handle view product details
            }}
          >
            <Eye size={16} className="text-blue-400 group-hover:text-blue-300" />
          </button>
        )}
      />
    </div>
  )

  const renderCashFlowReportContent = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-semibold text-white mb-1">Cash Flow Analysis</h3>
          <p className="text-gray-400 text-sm">Daily cash movement and balances</p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker
            value={[dateRange.start, dateRange.end]}
            onChange={(range) => {
              if (range) {
                setDateRange({ start: range[0], end: range[1] })
                setCurrentPage(1)
              }
            }}
          />
              <Button
                onClick={() => setIsExportDialogOpen(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Download size={18} />
                Export
              </Button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-750">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Cash In</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Cash Out</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Net Flow</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Closing Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {cashFlowReportData.map((flow, index) => (
                <tr key={index} className="hover:bg-gray-750 transition-colors">
                  <td className="px-6 py-4">
                    <div className="text-white">{new Date(flow.date).toLocaleDateString()}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-green-400 font-medium">
                      ${parseFloat(flow.cash_in.toString()).toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-red-400 font-medium">
                      ${parseFloat(flow.cash_out.toString()).toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`font-medium ${
                      parseFloat(flow.net_cash_flow.toString()) >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      ${parseFloat(flow.net_cash_flow.toString()).toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-white font-medium">
                      ${parseFloat(flow.closing_balance.toString()).toFixed(2)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderKPIsContent = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-semibold text-white mb-1">Key Performance Indicators</h3>
          <p className="text-gray-400 text-sm">Business metrics and targets</p>
        </div>
        <button className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors font-medium">
          <Plus size={18} />
          New KPI
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {kpiDefinitions.map(kpi => (
          <div key={kpi.id} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-colors">
            <div className="flex items-start justify-between mb-4">
              <h4 className="text-white font-medium">{kpi.kpi_name}</h4>
              <span className={`px-2 py-1 text-xs rounded-full ${
                kpi.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
              }`}>
                {kpi.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-sm text-gray-400 mb-4 line-clamp-2">{kpi.description}</p>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Target: {kpi.target_value} {kpi.unit_of_measure}</span>
              <button className="text-blue-400 hover:text-blue-300">
                View Trend
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderDashboardsContent = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-semibold text-white mb-1">Dashboard Widgets</h3>
          <p className="text-gray-400 text-sm">Customizable dashboard components</p>
        </div>
        <button className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors font-medium">
          <Plus size={18} />
          New Widget
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboardWidgets.map(widget => (
          <div key={widget.id} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-colors">
            <div className="flex items-start justify-between mb-4">
              <h4 className="text-white font-medium">{widget.widget_name}</h4>
              <span className={`px-2 py-1 text-xs rounded-full ${
                widget.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
              }`}>
                {widget.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="text-sm text-gray-400 mb-2">Type: {widget.widget_type}</div>
            <div className="text-sm text-gray-500">Data Source: {widget.data_source || 'N/A'}</div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderTabButtons = () => {
    const tabs = [
      { id: 'templates', label: 'Templates', icon: FileText },
      { id: 'custom', label: 'Custom', icon: Database },
      { id: 'scheduled', label: 'Scheduled', icon: Calendar },
      { id: 'executions', label: 'Executions', icon: Activity },
      { id: 'fiscal', label: 'Fiscal', icon: FileBarChart },
      { id: 'business', label: 'Business Days', icon: Calendar },
      { id: 'sales', label: 'Sales', icon: DollarSign },
      { id: 'inventory', label: 'Inventory', icon: Package },
      { id: 'cashflow', label: 'Cash Flow', icon: TrendingUp },
      { id: 'kpis', label: 'KPIs', icon: TrendingUp },
      { id: 'dashboards', label: 'Dashboards', icon: Grid }
    ]

    return (
      <div className="flex px-6">
        {tabs.map(tab => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id as TabType)
                setActiveSubTab(
                  tab.id === 'templates' ? 'all' :
                  tab.id === 'executions' ? 'all' :
                  tab.id === 'fiscal' ? 'z-reports' : 'all'
                )
                setCurrentPage(1)
              }}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-400 bg-gray-750'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-750'
              }`}
            >
              <Icon size={16} />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-900">
        <div className="bg-gray-800 px-6 py-4 flex items-center justify-between border-b border-gray-700">
          <div className="flex items-center gap-4">
            <Link to="/app" className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-gray-700 rounded-lg">
              <ChevronLeft size={20} />
            </Link>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-900/30 rounded-lg">
                <BarChart3 size={20} className="text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Reports & Analytics</h2>
                <p className="text-sm text-gray-400">Comprehensive business intelligence</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              onClick={() => {
                setRefreshKey(prev => prev + 1)
                toast.success('Data refreshed')
              }}
              title="Refresh"
            >
              <RefreshCw size={18} className="text-gray-400" />
            </button>
            <button
              onClick={handleMaximize}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              <Maximize size={18} className="text-gray-400" />
            </button>
          </div>
        </div>

        {!showDesigner && (
          <div className="bg-gray-800 border-b border-gray-700 overflow-x-auto">
            {renderTabButtons()}
          </div>
        )}

        <div className="p-6">
          {renderContent()}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={confirmDialogProps.title}
        message={confirmDialogProps.message}
        confirmText={confirmDialogProps.confirmText || 'Confirm'}
        cancelText={confirmDialogProps.cancelText || 'Cancel'}
        type={confirmDialogProps.type || 'warning'}
        onConfirm={confirmAction}
        onClose={closeConfirmDialog}
      />
    </ErrorBoundary>
  )
}